import {Easing} from '@gongxigames/framework/core/fragments/tween/Easing';
import {Interpolation} from '@gongxigames/framework/core/fragments/tween/Interpolation';
import {Updatable} from '@gongxigames/framework/core/fragments/update/Updatable';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';

export class Tween extends Updatable {
    protected _startTime = 0;
    protected _duration = 1000;
    protected _currentTime = 0;
    protected _isPlaying = false;
    private _repeat = 0;

    private _object: any;
    private _valuesStart: any = {};
    private _valuesEnd: any = {};
    private _valuesStartRepeat: any = {};
    private _yoyo = false;
    private _reversed = false;
    private _delayTime = 0;
    private _easingFunction: Function = Easing.Linear.None;
    private _interpolationFunction: Function = Interpolation.Linear;
    private _chainedTweens: Tween[] = [];
    private _onStartCallback?: Function;
    private _onStartCalledCallback?: Function;
    private _onStartCallbackFired = false;
    private _onUpdateCallback?: Function;
    private _onCompleteCallback?: Function;
    private _onStopCallback?: Function;
    private _skipFirstUpdate = false;

    constructor(object?: any) {
        super();

        if (object) {
            this._object = object;
        }
    }

    public to(properties: any, duration: number): Tween {
        if (duration !== undefined) {
            this._duration = duration;
        }

        this._valuesEnd = properties;

        this.parseStartValues(properties);

        return this;
    }

    public force(): void {
        for (const property in this._valuesEnd) {
            if (this._valuesEnd.hasOwnProperty(property)) {
                this._object[property] = this._valuesEnd[property];
            }
        }
    }

    public startAt(time = 0): Tween {
        super.start();

        this._onStartCalledCallback && this._onStartCalledCallback();

        this._onStartCallbackFired = false;

        this._startTime = time;
        this._currentTime = this._startTime;
        this._startTime += this._delayTime;

        for (const property in this._valuesEnd) {
            if (this._valuesEnd.hasOwnProperty(property)) {
                // Check if an Array was provided as property value
                if (this._valuesEnd[property] instanceof Array) {
                    if (this._valuesEnd[property].length === 0) {
                        continue;
                    }

                    // Create a local copy of the Array with the start value at the front
                    this._valuesEnd[property] = [this._object[property]].concat(this._valuesEnd[property]);
                }

                // If `to()` specifies a property that doesn't exist in the source object,
                // we should not set that property in the object
                if (this._valuesStart[property] === undefined) {
                    continue;
                }

                this._valuesStart[property] = this._object[property];

                if ((this._valuesStart[property] instanceof Array) === false) {
                    this._valuesStart[property] *= 1.0; // Ensures we're using numbers, not strings
                }

                this._valuesStartRepeat[property] = this._valuesStart[property] || 0;
            }
        }

        return this;
    }

    public start(): Tween {
        this.startAt();

        return this;
    }

    public stop(): void {
        if (!this._isPlaying) {
            return;
        }

        super.stop();

        this._onStopCallback?.call(this._object);
        this.stopChainedTweens();
    }

    public stopChainedTweens(): void {
        for (let i = 0, numChainedTweens = this._chainedTweens.length; i < numChainedTweens; i++) {
            this._chainedTweens[i].stop();
        }
    }

    public delay(amount: number): Tween {
        this._delayTime = amount;

        return this;
    }

    public repeat(times: number): Tween {
        this._repeat = times;

        return this;
    }

    public getRepeat(): number {
        return this._repeat;
    }

    public yoyo(yoyo: boolean): Tween {
        this._yoyo = yoyo;

        return this;
    }

    public easing(easing: Function): Tween {
        this._easingFunction = easing;

        return this;
    }

    public interpolation(interpolation: Function): Tween {
        this._interpolationFunction = interpolation;

        return this;
    }

    public chain(chain: Tween): Tween;

    public chain(chain: Tween[]): Tween;

    public chain(chain: any): Tween {
        // if Tween[]
        if (chain.length) {
            this._chainedTweens = chain;
        } else {
            this._chainedTweens = [chain];
        }

        return this;
    }

    public onStart(callback: Function): Tween {
        this._onStartCallback = callback;

        return this;
    }

    public onStartCalled(callback: Function): Tween {
        this._onStartCalledCallback = callback;

        return this;
    }

    public onUpdate(callback: Function): Tween {
        this._onUpdateCallback = callback;

        return this;
    }

    public onComplete(callback?: Function): Tween {
        this._onCompleteCallback = callback;

        return this;
    }

    public onStop(callback: Function): Tween {
        this._onStopCallback = callback;

        return this;
    }

    public update(dt: number): void {
        if (this._skipFirstUpdate) {
            this._skipFirstUpdate = false;

            return;
        }

        this._currentTime += dt;

        const time = this._currentTime;
        let property;
        let elapsed;

        if (time < this._startTime) {
            return;
        }

        if (!this._onStartCallbackFired) {
            this._onStartCallback?.call(this._object);
            this._onStartCallbackFired = true;
        }

        elapsed = (time - this._startTime) / this._duration;
        elapsed = elapsed > 1 ? 1 : elapsed;

        const value = this._easingFunction(elapsed);

        for (property in this._valuesEnd) {
            // Don't update properties that do not exist in the source object
            if (this._valuesStart[property] === undefined) {
                continue;
            }

            const start = this._valuesStart[property] || 0;
            let end = this._valuesEnd[property];

            if (end instanceof Array) {
                this._object[property] = this._interpolationFunction(end, value);
            } else {
                // Parses relative end values with start as base (e.g.: +10, -3)
                if (typeof (end) === 'string') {

                    if (end.charAt(0) === '+' || end.charAt(0) === '-') {
                        end = start + parseFloat(end);
                    } else {
                        end = parseFloat(end);
                    }
                }

                // Protect against non numeric properties.
                if (typeof (end) === 'number') {
                    this._object[property] = start + (end - start) * value;
                }
            }
        }

        this._onUpdateCallback?.call(this._object, value);

        if (elapsed === 1) {
            if (this._repeat > 0) {
                if (isFinite(this._repeat)) {
                    this._repeat--;
                }

                // Reassign starting values, restart by making startTime = now
                for (property in this._valuesStartRepeat) {
                    if (this._valuesStartRepeat.hasOwnProperty(property)) {
                        if (typeof (this._valuesEnd[property]) === 'string') {
                            this._valuesStartRepeat[property] = this._valuesStartRepeat[property] + parseFloat(this._valuesEnd[property]);
                        }

                        if (this._yoyo) {
                            const tmp = this._valuesStartRepeat[property];

                            this._valuesStartRepeat[property] = this._valuesEnd[property];
                            this._valuesEnd[property] = tmp;
                        }

                        this._valuesStart[property] = this._valuesStartRepeat[property];
                    }
                }

                if (this._yoyo) {
                    this._reversed = !this._reversed;
                }

                this._startTime = time + this._delayTime;
                this._currentTime = this._startTime;
            } else {
                this._onCompleteCallback?.call(this._object);

                for (let i = 0, numChainedTweens = this._chainedTweens.length; i < numChainedTweens; i++) {
                    this._updateManager!.add(this._chainedTweens[i]);

                    // Make the chained tweens start exactly at the time they should,
                    // even if the `update()` method was called way past the duration of the tween
                    this._chainedTweens[i]._skipFirstUpdate = true;
                    this._chainedTweens[i].startAt(this._startTime + this._duration);
                }

                super.stop();
            }
        }
    }

    public getPredictedProperty(time: number, property: string): number {
        let elapsed = (this._currentTime - this._startTime + time) / this._duration;

        elapsed = elapsed > 1 ? 1 : elapsed;

        const value = this._easingFunction(elapsed);
        const start = this._valuesStart[property] || 0;
        let end = this._valuesEnd[property];

        if (end instanceof Array) {
            return this._interpolationFunction(end, value);
        } else {
            // Parses relative end values with start as base (e.g.: +10, -3)
            if (typeof (end) === 'string') {

                if (end.charAt(0) === '+' || end.charAt(0) === '-') {
                    end = start + parseFloat(end);
                } else {
                    end = parseFloat(end);
                }
            }

            // Protect against non numeric properties.
            if (typeof (end) === 'number') {
                return start + (end - start) * value;
            }

            // TODO: refactor required!
            throw new Error('not implemented!');
        }
    }

    public get chainedTweens(): Tween[] {
        return this._chainedTweens;
    }

    public get object(): any {
        return this._object;
    }

    public get duration(): number {
        return this._duration;
    }

    public get isPlaying(): boolean {
        return this._isPlaying;
    }

    private parseStartValues(properties: any): void {
        for (const field in properties) {
            if (properties.hasOwnProperty(field)) {
                try {
                    this._valuesStart[field] = parseFloat(this.object[field]);
                } catch (e) {
                    consoleLogger.error(e);
                }
            }
        }
    }
}
